import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';

import { store } from './store';
import i18n from './i18n';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import PrivateRoute from './components/PrivateRoute';
import LoadingScreen from './components/LoadingScreen';
import Layout from './components/Layout';

// Public Pages
const Home = React.lazy(() => import('./pages/Home'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const HuntList = React.lazy(() => import('./pages/HuntList'));
const HuntDetail = React.lazy(() => import('./pages/HuntDetail'));
const Leaderboard = React.lazy(() => import('./pages/Leaderboard'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const Offerings = React.lazy(() => import('./pages/Offerings'));
const PartnerSignup = React.lazy(() => import('./pages/PartnerSignup'));
const PartnerAccount = React.lazy(() => import('./pages/PartnerAccount'));

// Protected Hunter Pages
const TeamDashboard = React.lazy(() => import('./pages/TeamDashboard'));
const QRScanner = React.lazy(() => import('./pages/QRScanner'));
const Payment = React.lazy(() => import('./pages/Payment'));
const Profile = React.lazy(() => import('./pages/Profile'));

// Protected Venue Pages (role-based)
const ShopDashboard = React.lazy(() => import('./pages/ShopDashboard'));

// Protected Organizer Pages (role-based)
const OrganizerDashboard = React.lazy(() => import('./pages/OrganizerDashboard'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const VenueDiscovery = React.lazy(() => import('./pages/VenueDiscovery'));

const theme = createTheme({
  palette: {
    primary: {
      main: '#FF6B35',
      light: '#FF8C5A',
      dark: '#E55A26',
    },
    secondary: {
      main: '#004E89',
      light: '#1A6FAF',
      dark: '#003664',
    },
    success: {
      main: '#4CAF50',
    },
    warning: {
      main: '#FFC107',
    },
    error: {
      main: '#F44336',
    },
    background: {
      default: '#F5F7FA',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
});

function App() {
  return (
    <HelmetProvider>
      <I18nextProvider i18n={i18n}>
        <Provider store={store}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
              <AuthProvider>
                <SocketProvider>
                      <Suspense fallback={<LoadingScreen />}>
                        <Routes>
                          <Route path="/" element={<Layout />}>
                            {/* Public Routes */}
                            <Route index element={<Home />} />
                            <Route path="login" element={<Login />} />
                            <Route path="register" element={<Register />} />
                            <Route path="forgot-password" element={<ForgotPassword />} />
                            <Route path="reset-password" element={<ResetPassword />} />
                            <Route path="onboarding" element={<Navigate to="/hunts" replace />} />
                            <Route path="hunts" element={<HuntList />} />
                            <Route path="hunts/:huntId" element={<HuntDetail />} />
                            <Route path="hunts/:huntId/leaderboard" element={<Leaderboard />} />
                            <Route path="offerings" element={<Offerings />} />
                            <Route path="partner/signup" element={<PartnerSignup />} />

                            {/* Protected Hunter Routes */}
                            <Route element={<PrivateRoute />}>
                              <Route path="team/:teamId" element={<TeamDashboard />} />
                              <Route path="team/:teamId/scan" element={<QRScanner />} />
                              <Route path="team/:teamId/payment" element={<Payment />} />
                              <Route path="profile" element={<Profile />} />
                            </Route>
                            <Route element={<PrivateRoute requiredRole={['hunter', 'organizer', 'admin']} />}>
                              <Route path="venue-discovery" element={<VenueDiscovery />} />
                            </Route>

                            {/* Protected Venue Routes */}
                            <Route element={<PrivateRoute requiredRole={['shop_owner', 'shop_employee', 'admin']} />}>
                              <Route path="venue/dashboard" element={<ShopDashboard />} />
                            </Route>
                            <Route element={<PrivateRoute requiredRole={['shop_owner', 'admin']} />}>
                              <Route path="partner/account" element={<PartnerAccount />} />
                            </Route>

                            {/* Protected Organizer Routes */}
                            <Route element={<PrivateRoute requiredRole={['organizer', 'admin']} />}>
                              <Route path="organizer/dashboard" element={<OrganizerDashboard />} />
                            </Route>
                            <Route element={<PrivateRoute requiredRole="admin" />}>
                              <Route path="admin/dashboard" element={<AdminDashboard />} />
                            </Route>

                            {/* 404 */}
                            <Route path="404" element={<NotFound />} />
                            <Route path="*" element={<Navigate to="/404" replace />} />
                          </Route>
                        </Routes>
                      </Suspense>
                    <Toaster
                      position="top-right"
                      toastOptions={{
                        duration: 4000,
                        style: {
                          background: '#363636',
                          color: '#fff',
                        },
                        success: {
                          style: {
                            background: '#4CAF50',
                          },
                        },
                        error: {
                          style: {
                            background: '#F44336',
                          },
                        },
                      }}
                    />
                </SocketProvider>
              </AuthProvider>
            </Router>
          </ThemeProvider>
        </Provider>
      </I18nextProvider>
    </HelmetProvider>
  );
}

export default App;
